import React, { useMemo } from 'react';
import styled from 'styled-components';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import PageButton, { PageButtonTone } from './PageButton';

type PageToken = number | 'start-ellipsis' | 'end-ellipsis';

export interface PaginationProps {
  /** 1-based current page. */
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  totalItems?: number;
  pageSize?: number;
  siblingCount?: number;
  disabled?: boolean;
  showSummary?: boolean;
  className?: string;
  /** Cor de marca da página ativa e das setas. Default: primary (Azul Urbano). */
  variant?: PageButtonTone;
  /** Quando informado, exibe um seletor de itens por página. */
  pageSizeOptions?: number[];
  onPageSizeChange?: (pageSize: number) => void;
}

const PaginationWrapper = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-top: 1rem;
  color: ${(p) => p.theme.textMuted};
  font-size: 0.875rem;

  @media (max-width: 640px) {
    align-items: stretch;
    flex-direction: column;
  }
`;

const Summary = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  color: ${(p) => p.theme.textMuted};
  font-size: 0.8125rem;
  line-height: 1.3;

  strong {
    color: ${(p) => p.theme.textSecondary};
    font-weight: 700;
    font-size: 0.875rem;
  }
`;

const RightGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;

  @media (max-width: 640px) {
    justify-content: space-between;
  }
`;

const PageSizeControl = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: ${(p) => p.theme.textMuted};
  font-size: 0.8125rem;
  white-space: nowrap;

  select {
    border: 1px solid ${(p) => p.theme.border};
    border-radius: ${(p) => p.theme.radius};
    background: ${(p) => p.theme.surface};
    color: ${(p) => p.theme.textSecondary};
    font-size: 0.8125rem;
    font-weight: 600;
    font-family: inherit;
    padding: 0.4rem 0.5rem;
    cursor: pointer;

    &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px ${(p) => p.theme.primaryLight};
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }
  }
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.375rem;
  flex-wrap: wrap;

  @media (max-width: 640px) {
    justify-content: flex-start;
  }
`;

const Ellipsis = styled.span`
  min-width: 2.25rem;
  height: 2.25rem;
  color: ${(p) => p.theme.textMuted};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
`;

const range = (start: number, end: number): number[] => {
  if (end < start) return [];
  return Array.from({ length: end - start + 1 }, (_, index) => start + index);
};

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const buildPageTokens = (
  currentPage: number,
  totalPages: number,
  siblingCount: number
): PageToken[] => {
  const totalPageNumbers = siblingCount * 2 + 5;

  if (totalPageNumbers >= totalPages) {
    return range(1, totalPages);
  }

  const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
  const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPages);
  const showLeftEllipsis = leftSiblingIndex > 2;
  const showRightEllipsis = rightSiblingIndex < totalPages - 1;

  if (!showLeftEllipsis && showRightEllipsis) {
    const leftRange = range(1, 3 + siblingCount * 2);
    return [...leftRange, 'end-ellipsis', totalPages];
  }

  if (showLeftEllipsis && !showRightEllipsis) {
    const rightRange = range(totalPages - (2 + siblingCount * 2), totalPages);
    return [1, 'start-ellipsis', ...rightRange];
  }

  return [1, 'start-ellipsis', ...range(leftSiblingIndex, rightSiblingIndex), 'end-ellipsis', totalPages];
};

/**
 * Paginação compartilhada do backoffice/admin. Usa o `PageButton` (@kardume/ui),
 * então todo o visual vem dos tokens do `@kardume/theme`.
 *
 * `currentPage` é 1-based. Para a `Page` (Spring Data) do core, converta:
 * `currentPage={page.number + 1}` e no `onPageChange` use `page - 1`.
 */
const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  totalItems,
  pageSize,
  siblingCount = 1,
  disabled = false,
  showSummary = true,
  className,
  variant = 'primary',
  pageSizeOptions,
  onPageSizeChange,
}) => {
  const pageCount = Math.max(0, totalPages);
  const safeCurrentPage = pageCount > 0 ? clamp(currentPage, 1, pageCount) : 1;

  const pages = useMemo(
    () => buildPageTokens(safeCurrentPage, pageCount, siblingCount),
    [safeCurrentPage, pageCount, siblingCount]
  );

  const showPageSizeSelector = !!(pageSizeOptions && pageSizeOptions.length > 0 && onPageSizeChange);
  const showPageControls = pageCount > 1;

  // Nada a exibir: sem controles de página e sem seletor de tamanho.
  if (!showPageControls && !showPageSizeSelector) return null;

  const handlePageChange = (page: number) => {
    if (disabled || page === safeCurrentPage || page < 1 || page > pageCount) return;
    onPageChange(page);
  };

  const startItem =
    totalItems != null && pageSize ? (safeCurrentPage - 1) * pageSize + 1 : undefined;
  const endItem =
    totalItems != null && pageSize ? Math.min(safeCurrentPage * pageSize, totalItems) : undefined;

  return (
    <PaginationWrapper className={className} aria-label="Paginação">
      {showSummary && (
        <Summary>
          <strong>
            Página {safeCurrentPage} de {Math.max(pageCount, 1)}
          </strong>
          {startItem != null && endItem != null && (
            <span>
              {startItem}–{endItem} de {totalItems} registros
            </span>
          )}
        </Summary>
      )}

      <RightGroup>
        {showPageSizeSelector && (
          <PageSizeControl>
            Registros por página
            <select
              value={pageSize != null ? String(pageSize) : ''}
              disabled={disabled}
              onChange={(event) => onPageSizeChange?.(Number(event.target.value))}
              aria-label="Registros por página"
            >
              {pageSizeOptions!.map((option) => (
                <option key={option} value={String(option)}>
                  {option}
                </option>
              ))}
            </select>
          </PageSizeControl>
        )}

        {showPageControls && (
          <Actions>
            <PageButton
              tone={variant}
              aria-label="Página anterior"
              disabled={disabled || safeCurrentPage === 1}
              onClick={() => handlePageChange(safeCurrentPage - 1)}
            >
              <ChevronLeft size={18} />
            </PageButton>

            {pages.map((page) =>
              typeof page === 'number' ? (
                <PageButton
                  key={page}
                  tone={variant}
                  active={page === safeCurrentPage}
                  aria-current={page === safeCurrentPage ? 'page' : undefined}
                  disabled={disabled || page === safeCurrentPage}
                  onClick={() => handlePageChange(page)}
                >
                  {page}
                </PageButton>
              ) : (
                <Ellipsis key={page}>…</Ellipsis>
              )
            )}

            <PageButton
              tone={variant}
              aria-label="Próxima página"
              disabled={disabled || safeCurrentPage === pageCount}
              onClick={() => handlePageChange(safeCurrentPage + 1)}
            >
              <ChevronRight size={18} />
            </PageButton>
          </Actions>
        )}
      </RightGroup>
    </PaginationWrapper>
  );
};

export default Pagination;
